<template>
  <div class="analytics-panel">
    <div v-if="error" class="analytics-error">{{ error }}</div>
    <div v-else-if="loading && !report" class="analytics-empty">Loading activity…</div>
    <template v-else-if="report">

      <div v-if="section === 'overview'">
        <p class="analytics-note">Scheduled tasks and hooks are how many exist now. Runs, jobs, and requests follow the range in the bar.</p>

        <div class="analytics-section">
          <div class="analytics-section-title">Set up now</div>
          <div class="stat-cards">
            <button type="button" class="stat-card stat-card-button" @click="$emit('open', 'schedules')">
              <div class="stat-card-value">{{ formatNumber(report.schedules.total) }}</div>
              <div class="stat-card-label">scheduled tasks</div>
              <div class="stat-card-note">{{ formatNumber(report.schedules.enabled) }} enabled</div>
            </button>
            <button type="button" class="stat-card stat-card-button" @click="$emit('open', 'hooks')">
              <div class="stat-card-value">{{ formatNumber(report.hooks.configured) }}</div>
              <div class="stat-card-label">spreadsheet hooks</div>
            </button>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">What ran</div>
          <div class="stat-cards">
            <button type="button" class="stat-card stat-card-button" @click="$emit('open', 'schedules')">
              <div class="stat-card-value">{{ formatNumber(report.schedules.runs.total) }}</div>
              <div class="stat-card-label">schedule runs</div>
              <div class="stat-card-note">{{ failNote(report.schedules.runs.error) }}</div>
            </button>
            <button type="button" class="stat-card stat-card-button" @click="$emit('open', 'hooks')">
              <div class="stat-card-value">{{ formatNumber(report.hooks.runs.total) }}</div>
              <div class="stat-card-label">hook runs</div>
              <div class="stat-card-note">{{ failNote(report.hooks.runs.error) }}</div>
            </button>
            <button type="button" class="stat-card stat-card-button" @click="$emit('open', 'automations')">
              <div class="stat-card-value">{{ formatNumber(report.automations.runs.total) }}</div>
              <div class="stat-card-label">automation runs</div>
              <div class="stat-card-note">{{ failNote(report.automations.runs.error) }}</div>
            </button>
            <button type="button" class="stat-card stat-card-button" @click="$emit('open', 'workflows')">
              <div class="stat-card-value">{{ formatNumber(report.workflows.runs.total) }}</div>
              <div class="stat-card-label">workflow runs</div>
              <div class="stat-card-note">{{ failNote(report.workflows.runs.error) }}</div>
            </button>
            <button type="button" class="stat-card stat-card-button" @click="$emit('open', 'jobs')">
              <div class="stat-card-value">{{ formatNumber(report.jobs.total) }}</div>
              <div class="stat-card-label">background jobs</div>
              <div class="stat-card-note">{{ progressNote(report.jobs) }}</div>
            </button>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Requests</div>
          <div class="stat-cards">
            <button type="button" class="stat-card stat-card-button" @click="$emit('open', 'spreadsheets')">
              <div class="stat-card-value">{{ formatNumber(report.traffic.spreadsheet) }}</div>
              <div class="stat-card-label">spreadsheet events</div>
            </button>
            <button type="button" class="stat-card stat-card-button" @click="$emit('open', 'pages')">
              <div class="stat-card-value">{{ formatNumber(report.traffic.page) }}</div>
              <div class="stat-card-label">live app events</div>
            </button>
            <button type="button" class="stat-card stat-card-button" @click="$emit('open', 'api')">
              <div class="stat-card-value">{{ formatNumber(report.traffic.apiRequests) }}</div>
              <div class="stat-card-label">API requests</div>
              <div class="stat-card-note">{{ failNote(report.traffic.apiErrors) }}</div>
            </button>
            <button type="button" class="stat-card stat-card-button" @click="$emit('open', 'audit')">
              <div class="stat-card-value">{{ formatNumber(report.traffic.audit) }}</div>
              <div class="stat-card-label">audit events</div>
            </button>
          </div>
        </div>
      </div>

      <div v-else-if="section === 'schedules'">
        <div class="stat-cards analytics-section">
          <div class="stat-card">
            <div class="stat-card-value">{{ formatNumber(report.schedules.total) }}</div>
            <div class="stat-card-label">scheduled tasks</div>
          </div>
          <div class="stat-card stat-card-success">
            <div class="stat-card-value">{{ formatNumber(report.schedules.enabled) }}</div>
            <div class="stat-card-label">enabled</div>
          </div>
          <div class="stat-card">
            <div class="stat-card-value">{{ formatNumber(report.schedules.disabled) }}</div>
            <div class="stat-card-label">disabled</div>
          </div>
          <div class="stat-card">
            <div class="stat-card-value">{{ formatNumber(report.schedules.runs.total) }}</div>
            <div class="stat-card-label">runs in range</div>
          </div>
          <div class="stat-card stat-card-error">
            <div class="stat-card-value">{{ formatNumber(report.schedules.runs.error) }}</div>
            <div class="stat-card-label">failed runs</div>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Tasks</div>
          <div v-if="report.schedules.tasks.length === 0" class="analytics-empty">No scheduled tasks are set up.</div>
          <table v-else class="analytics-table">
            <thead>
              <tr>
                <th>task</th>
                <th>spreadsheet</th>
                <th>action</th>
                <th>timing</th>
                <th>enabled</th>
                <th>runs</th>
                <th>failed</th>
                <th>last run</th>
                <th>next run</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="task in report.schedules.tasks" :key="task.id || task.title">
                <td>{{ task.title }}</td>
                <td>{{ task.spreadsheet }}</td>
                <td>{{ labelAction(task.actionType) }}</td>
                <td>{{ labelTiming(task) }}</td>
                <td>{{ task.enabled ? 'yes' : 'no' }}</td>
                <td>{{ formatNumber(task.runs) }}</td>
                <td>{{ formatNumber(task.errors) }}</td>
                <td>{{ formatWhen(task.lastRunAt) }}</td>
                <td>{{ task.enabled ? formatWhen(task.nextRunAt) : '–' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Runs over time</div>
          <div v-if="report.schedules.days.length === 0" class="analytics-empty">No runs in this range.</div>
          <div v-else class="bar-list">
            <div class="bar-row" v-for="day in report.schedules.days" :key="day.date">
              <div class="bar-label">{{ dayLabel(day.date) }}</div>
              <div class="bar-track"><div class="bar-fill" :style="{ width: dayWidth(report.schedules.days, day.count) }"></div></div>
              <div class="bar-count">{{ formatNumber(day.count) }}</div>
            </div>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Recent runs</div>
          <div v-if="report.schedules.recent.length === 0" class="analytics-empty">No schedule runs in this range.</div>
          <table v-else class="analytics-table">
            <thead>
              <tr>
                <th>when</th>
                <th>task</th>
                <th>action</th>
                <th>status</th>
                <th>records</th>
                <th>detail</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in report.schedules.recent" :key="index">
                <td>{{ formatWhen(row.startedAt) }}</td>
                <td>{{ row.title || '–' }}</td>
                <td>{{ labelAction(row.actionType) }}</td>
                <td><span class="status-badge" :class="statusClass(row.status)">{{ row.status || '–' }}</span></td>
                <td>{{ row.recordsAffected == null ? '–' : formatNumber(row.recordsAffected) }}</td>
                <td>{{ row.error || '–' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else-if="section === 'hooks'">
        <div class="stat-cards analytics-section">
          <div class="stat-card">
            <div class="stat-card-value">{{ formatNumber(report.hooks.configured) }}</div>
            <div class="stat-card-label">hooks set up</div>
          </div>
          <div class="stat-card">
            <div class="stat-card-value">{{ formatNumber(report.hooks.runs.total) }}</div>
            <div class="stat-card-label">runs in range</div>
          </div>
          <div class="stat-card stat-card-success">
            <div class="stat-card-value">{{ formatNumber(report.hooks.runs.success) }}</div>
            <div class="stat-card-label">succeeded</div>
          </div>
          <div class="stat-card stat-card-error">
            <div class="stat-card-value">{{ formatNumber(report.hooks.runs.error) }}</div>
            <div class="stat-card-label">failed</div>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Hooks</div>
          <div v-if="report.hooks.items.length === 0" class="analytics-empty">No spreadsheet hooks are set up, and none ran in this range.</div>
          <table v-else class="analytics-table">
            <thead>
              <tr>
                <th>spreadsheet</th>
                <th>workflow</th>
                <th>when</th>
                <th>set up</th>
                <th>runs</th>
                <th>failed</th>
                <th>last run</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(hook, index) in report.hooks.items" :key="index">
                <td>{{ hook.spreadsheetTitle || hook.spreadsheet }}</td>
                <td>{{ hook.workflowTitle || hook.workflow }}</td>
                <td>{{ labelEvent(hook.event) }}</td>
                <td>{{ hook.configured ? 'yes' : 'removed' }}</td>
                <td>{{ formatNumber(hook.runs) }}</td>
                <td>{{ formatNumber(hook.errors) }}</td>
                <td>{{ formatWhen(hook.lastRunAt) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Runs over time</div>
          <div v-if="report.hooks.days.length === 0" class="analytics-empty">No runs in this range.</div>
          <div v-else class="bar-list">
            <div class="bar-row" v-for="day in report.hooks.days" :key="day.date">
              <div class="bar-label">{{ dayLabel(day.date) }}</div>
              <div class="bar-track"><div class="bar-fill" :style="{ width: dayWidth(report.hooks.days, day.count) }"></div></div>
              <div class="bar-count">{{ formatNumber(day.count) }}</div>
            </div>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Recent hook runs</div>
          <div v-if="report.hooks.recent.length === 0" class="analytics-empty">No hook runs in this range.</div>
          <table v-else class="analytics-table">
            <thead>
              <tr>
                <th>when</th>
                <th>spreadsheet</th>
                <th>workflow</th>
                <th>event</th>
                <th>status</th>
                <th>record</th>
                <th>detail</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in report.hooks.recent" :key="index">
                <td>{{ formatWhen(row.finishedAt) }}</td>
                <td>{{ row.spreadsheetTitle || row.spreadsheet || '–' }}</td>
                <td>{{ row.workflowTitle || row.workflow || '–' }}</td>
                <td>{{ labelEvent(row.event) }}</td>
                <td><span class="status-badge" :class="statusClass(row.status)">{{ row.status || '–' }}</span></td>
                <td>{{ row.recordId == null ? '–' : row.recordId }}</td>
                <td>{{ row.message || '–' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else-if="section === 'automations'">
        <div class="stat-cards analytics-section">
          <div class="stat-card">
            <div class="stat-card-value">{{ formatNumber(report.automations.runs.total) }}</div>
            <div class="stat-card-label">runs in range</div>
          </div>
          <div class="stat-card stat-card-success">
            <div class="stat-card-value">{{ formatNumber(report.automations.runs.success) }}</div>
            <div class="stat-card-label">succeeded</div>
          </div>
          <div class="stat-card stat-card-error">
            <div class="stat-card-value">{{ formatNumber(report.automations.runs.error) }}</div>
            <div class="stat-card-label">failed</div>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Automations</div>
          <div v-if="report.automations.rows.length === 0" class="analytics-empty">No automations ran in this range.</div>
          <table v-else class="analytics-table">
            <thead>
              <tr>
                <th>automation</th>
                <th>type</th>
                <th>trigger</th>
                <th>runs</th>
                <th>failed</th>
                <th>avg duration</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in report.automations.rows" :key="index">
                <td>{{ row.title || row.uniqueTitle }}</td>
                <td>{{ labelType(row.type) }}</td>
                <td><span class="status-badge" :class="statusClass(row.trigger)">{{ labelTrigger(row.trigger) }}</span></td>
                <td>{{ formatNumber(row.runs) }}</td>
                <td>{{ formatNumber(row.errors) }}</td>
                <td>{{ formatDuration(row.avgDurationMs) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">By trigger</div>
          <div v-if="report.automations.byTrigger.length === 0" class="analytics-empty">No automations ran in this range.</div>
          <div v-else class="bar-list">
            <div class="bar-row" v-for="(row, index) in report.automations.byTrigger" :key="index">
              <div class="bar-label">{{ labelTrigger(row.trigger) }}</div>
              <div class="bar-track"><div class="bar-fill" :style="{ width: countWidth(report.automations.byTrigger, row.count) }"></div></div>
              <div class="bar-count">{{ formatNumber(row.count) }}</div>
            </div>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Runs over time</div>
          <div v-if="report.automations.days.length === 0" class="analytics-empty">No runs in this range.</div>
          <div v-else class="bar-list">
            <div class="bar-row" v-for="day in report.automations.days" :key="day.date">
              <div class="bar-label">{{ dayLabel(day.date) }}</div>
              <div class="bar-track"><div class="bar-fill" :style="{ width: dayWidth(report.automations.days, day.count) }"></div></div>
              <div class="bar-count">{{ formatNumber(day.count) }}</div>
            </div>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Recent runs</div>
          <div v-if="report.automations.recent.length === 0" class="analytics-empty">No automations ran in this range.</div>
          <table v-else class="analytics-table">
            <thead>
              <tr>
                <th>when</th>
                <th>automation</th>
                <th>trigger</th>
                <th>status</th>
                <th>duration</th>
                <th>detail</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in report.automations.recent" :key="index">
                <td>{{ formatWhen(row.executedAt) }}</td>
                <td>{{ row.title || row.uniqueTitle || '–' }}</td>
                <td>{{ labelTrigger(row.trigger) }}</td>
                <td><span class="status-badge" :class="statusClass(row.status)">{{ row.status || '–' }}</span></td>
                <td>{{ formatDuration(row.duration) }}</td>
                <td>{{ row.error || '–' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else-if="section === 'workflows'">
        <div class="stat-cards analytics-section">
          <div class="stat-card">
            <div class="stat-card-value">{{ formatNumber(report.workflows.runs.total) }}</div>
            <div class="stat-card-label">runs in range</div>
          </div>
          <div class="stat-card stat-card-success">
            <div class="stat-card-value">{{ formatNumber(report.workflows.runs.success) }}</div>
            <div class="stat-card-label">succeeded</div>
          </div>
          <div class="stat-card stat-card-error">
            <div class="stat-card-value">{{ formatNumber(report.workflows.runs.error) }}</div>
            <div class="stat-card-label">failed</div>
          </div>
          <div class="stat-card">
            <div class="stat-card-value">{{ formatNumber(report.workflows.runs.stopped) }}</div>
            <div class="stat-card-label">stopped</div>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Workflows</div>
          <div v-if="report.workflows.rows.length === 0" class="analytics-empty">No workflows ran in this range.</div>
          <table v-else class="analytics-table">
            <thead>
              <tr>
                <th>workflow</th>
                <th>trigger</th>
                <th>runs</th>
                <th>failed</th>
                <th>stopped</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in report.workflows.rows" :key="index">
                <td>{{ row.title || row.uniqueTitle }}</td>
                <td>{{ labelTrigger(row.trigger) }}</td>
                <td>{{ formatNumber(row.runs) }}</td>
                <td>{{ formatNumber(row.errors) }}</td>
                <td>{{ formatNumber(row.stopped) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">By trigger</div>
          <div v-if="report.workflows.byTrigger.length === 0" class="analytics-empty">No workflows ran in this range.</div>
          <div v-else class="bar-list">
            <div class="bar-row" v-for="(row, index) in report.workflows.byTrigger" :key="index">
              <div class="bar-label">{{ labelTrigger(row.trigger) }}</div>
              <div class="bar-track"><div class="bar-fill" :style="{ width: countWidth(report.workflows.byTrigger, row.count) }"></div></div>
              <div class="bar-count">{{ formatNumber(row.count) }}</div>
            </div>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Runs over time</div>
          <div v-if="report.workflows.days.length === 0" class="analytics-empty">No runs in this range.</div>
          <div v-else class="bar-list">
            <div class="bar-row" v-for="day in report.workflows.days" :key="day.date">
              <div class="bar-label">{{ dayLabel(day.date) }}</div>
              <div class="bar-track"><div class="bar-fill" :style="{ width: dayWidth(report.workflows.days, day.count) }"></div></div>
              <div class="bar-count">{{ formatNumber(day.count) }}</div>
            </div>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Recent runs</div>
          <div v-if="report.workflows.recent.length === 0" class="analytics-empty">No workflows ran in this range.</div>
          <table v-else class="analytics-table">
            <thead>
              <tr>
                <th>when</th>
                <th>workflow</th>
                <th>trigger</th>
                <th>status</th>
                <th>record</th>
                <th>detail</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in report.workflows.recent" :key="index">
                <td>{{ formatWhen(row.finishedAt) }}</td>
                <td>{{ row.title || row.uniqueTitle || '–' }}</td>
                <td>{{ labelTrigger(row.trigger) }}</td>
                <td><span class="status-badge" :class="statusClass(row.status)">{{ row.status || '–' }}</span></td>
                <td>{{ row.recordId == null ? '–' : row.recordId }}</td>
                <td>{{ row.message || '–' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else-if="section === 'jobs'">
        <div class="stat-cards analytics-section">
          <div class="stat-card">
            <div class="stat-card-value">{{ formatNumber(report.jobs.pending + report.jobs.running) }}</div>
            <div class="stat-card-label">in progress now</div>
            <div class="stat-card-note">{{ formatNumber(report.jobs.running) }} running</div>
          </div>
          <div class="stat-card">
            <div class="stat-card-value">{{ formatNumber(report.jobs.total) }}</div>
            <div class="stat-card-label">created in range</div>
          </div>
          <div class="stat-card stat-card-success">
            <div class="stat-card-value">{{ formatNumber(report.jobs.completed) }}</div>
            <div class="stat-card-label">completed</div>
          </div>
          <div class="stat-card stat-card-error">
            <div class="stat-card-value">{{ formatNumber(report.jobs.failed) }}</div>
            <div class="stat-card-label">failed</div>
          </div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">By type</div>
          <div v-if="report.jobs.byType.length === 0" class="analytics-empty">No background jobs were created in this range.</div>
          <table v-else class="analytics-table">
            <thead>
              <tr>
                <th>type</th>
                <th>jobs</th>
                <th>completed</th>
                <th>failed</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in report.jobs.byType" :key="row.type">
                <td>{{ labelJob(row.type) }}</td>
                <td>{{ formatNumber(row.total) }}</td>
                <td>{{ formatNumber(row.completed) }}</td>
                <td>{{ formatNumber(row.failed) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Recent jobs</div>
          <div v-if="report.jobs.recent.length === 0" class="analytics-empty">No background jobs in this range.</div>
          <table v-else class="analytics-table">
            <thead>
              <tr>
                <th>when</th>
                <th>type</th>
                <th>spreadsheet</th>
                <th>status</th>
                <th>detail</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in report.jobs.recent" :key="row.id">
                <td>{{ formatWhen(row.createdAt) }}</td>
                <td>{{ labelJob(row.type) }}</td>
                <td>{{ row.spreadsheet || '–' }}</td>
                <td><span class="status-badge" :class="statusClass(row.status)">{{ row.status || '–' }}</span></td>
                <td>{{ row.error || '–' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </template>
  </div>
</template>

<script>

const ACTION_LABELS = {
  bulk_update: 'bulk update',
  bulk_delete: 'bulk delete',
  recalculate_formulas: 'recalculate formulas',
  trigger_automation: 'trigger automation',
  trigger_step_workflow: 'trigger workflow'
}

const TYPE_LABELS = {
  http_request: 'HTTP request',
  playwright: 'Playwright',
  send_email: 'email',
  write_records: 'write records',
  webhook: 'webhook'
}

const TRIGGER_LABELS = {
  manual: 'manual',
  scheduled: 'schedule',
  schedule: 'schedule',
  form_action: 'form',
  form_submission: 'form',
  webhook: 'webhook',
  record_created: 'record created',
  record_updated: 'record updated',
  record_deleted: 'record deleted',
  record_button: 'record button',
  button: 'button'
}

const EVENT_LABELS = {
  create: 'record created',
  update: 'record updated',
  delete: 'record deleted'
}

const JOB_LABELS = {
  formula_recalc: 'formula recalculation',
  app_copy: 'app copy'
}

export default {
  name: 'AnalyticsActivity',
  props: {
    section: String,
    report: Object,
    loading: Boolean,
    error: String
  },
  emits: ['open'],
  methods: {
    formatNumber (value) {
      return Number(value || 0).toLocaleString()
    },
    formatWhen (value) {
      if (!value) return '–'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '–'
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    },
    formatDuration (value) {
      return value == null ? '–' : Math.round(value) + ' ms'
    },
    dayLabel (dateStr) {
      const date = new Date(dateStr + 'T00:00:00')
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },
    dayWidth (days, count) {
      const max = Math.max(...days.map((day) => day.count), 1)
      return Math.max((count / max) * 100, 3) + '%'
    },
    countWidth (rows, count) {
      const max = Math.max(...rows.map((row) => row.count), 1)
      return Math.max((count / max) * 100, 3) + '%'
    },
    failNote (count) {
      return count ? this.formatNumber(count) + ' failed' : ''
    },
    progressNote (jobs) {
      const active = (jobs.pending || 0) + (jobs.running || 0)
      if (active) return this.formatNumber(active) + ' in progress'
      return this.failNote(jobs.failed)
    },
    labelAction (action) {
      return ACTION_LABELS[action] || action || '–'
    },
    labelType (type) {
      return TYPE_LABELS[type] || type || '–'
    },
    labelTrigger (trigger) {
      if (TRIGGER_LABELS[trigger]) return TRIGGER_LABELS[trigger]
      if (!trigger) return 'manual'
      return String(trigger).replace(/_/g, ' ')
    },
    labelEvent (event) {
      return EVENT_LABELS[event] || event || '–'
    },
    labelJob (type) {
      return JOB_LABELS[type] || type || '–'
    },
    labelTiming (task) {
      if (task.scheduleType === 'once') return 'once'
      return task.frequency || 'recurring'
    },
    statusClass (status) {
      if (status === 'success' || status === 'completed') return 'status-green'
      if (status === 'error' || status === 'failed') return 'status-red'
      if (status === 'stopped' || status === 'running' || status === 'scheduled' || status === 'schedule') return 'status-orange'
      if (status === 'webhook' || status === 'manual') return 'status-blue'
      return 'status-gray'
    }
  }
}
</script>

<style scoped>

.analytics-panel {
  max-width: 1040px;
}

.analytics-section,
:deep(.analytics-section) {
  margin-bottom: 28px;
}

.analytics-section-title,
:deep(.analytics-section-title) {
  font-size: 0.78rem;
  font-weight: 600;
  color: #a0a09e;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
}

.analytics-empty,
:deep(.analytics-empty),
.analytics-error,
.analytics-note {
  color: #a8a8a4;
  font-size: 0.8rem;
  padding: 4px 0 12px;
}

.analytics-error {
  color: #e05050;
}

.stat-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.stat-card {
  background: #1a1a17;
  border: 1px solid #2a2a27;
  border-radius: 6px;
  padding: 12px 16px;
  min-width: 120px;
}

.stat-card-button {
  text-align: left;
  color: inherit;
  cursor: pointer;
  font: inherit;
}

.stat-card-button:hover {
  border-color: #484845;
  background: #222220;
}

.stat-card-value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #e8e8e6;
  line-height: 1.2;
}

.stat-card-label,
.stat-card-note {
  font-size: 0.7rem;
  color: #707070;
  margin-top: 2px;
}

.stat-card-success {
  border-color: rgba(108, 198, 68, 0.3);
}

.stat-card-success .stat-card-value {
  color: #6cc644;
}

.stat-card-error {
  border-color: rgba(224, 80, 80, 0.3);
}

.stat-card-error .stat-card-value {
  color: #e05050;
}

.analytics-table,
:deep(.analytics-table) {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.analytics-table th,
:deep(.analytics-table th) {
  text-align: left;
  color: #707070;
  font-weight: 500;
  padding: 6px 10px;
  border-bottom: 1px solid #2a2a27;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.analytics-table td,
:deep(.analytics-table td) {
  padding: 7px 10px;
  color: #a0a09e;
  border-bottom: 1px solid #1e1e1b;
}

.analytics-table tr:hover td,
:deep(.analytics-table tr:hover td) {
  background: #1a1a17;
}

.status-badge,
:deep(.status-badge) {
  font-size: 0.68rem;
  padding: 2px 7px;
  border-radius: 3px;
  font-weight: 500;
}

.status-blue, :deep(.status-blue) { color: #4a9eff; background: rgba(74, 158, 255, 0.1); }
.status-green, :deep(.status-green) { color: #6cc644; background: rgba(108, 198, 68, 0.1); }
.status-orange, :deep(.status-orange) { color: #e8a020; background: rgba(232, 160, 32, 0.1); }
.status-red, :deep(.status-red) { color: #e05050; background: rgba(224, 80, 80, 0.1); }
.status-gray, :deep(.status-gray) { color: #707070; background: rgba(112, 112, 112, 0.1); }

.bar-list,
:deep(.bar-list) {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.bar-row,
:deep(.bar-row) {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bar-label,
:deep(.bar-label) {
  width: 110px;
  flex-shrink: 0;
  text-align: right;
  font-size: 0.72rem;
  color: #909090;
}

.bar-track,
:deep(.bar-track) {
  flex: 1;
  background: #141412;
  border-radius: 3px;
  height: 18px;
  overflow: hidden;
}

.bar-fill,
:deep(.bar-fill) {
  height: 100%;
  background: linear-gradient(90deg, #4a9eff88, #4a9eff);
  border-radius: 3px;
}

.bar-count,
:deep(.bar-count) {
  width: 48px;
  font-size: 0.72rem;
  color: #707070;
  flex-shrink: 0;
}
</style>
